// The site while a visitor is on the tour (P4): the menu button beats, and
// the row of faces in the corner is the way on and the way off.
//
// Being on the tour is the tab's sessionStorage `hyper-tour-following`, the
// key hyper-tour-follow and join-page already keep; a change of it on this
// page arrives as `hyper-tour:joined { joined }`. Leaving is
// `hyper-tour:leave` on window: hyper-tour-follow lets the camera go, and
// whatever publishes this visitor into the room stops on the same event.
//
// While on the tour the page also folds its own chrome away — the floor-map
// panel and its switches, the Tower's card list, the panels that ask the
// visitor to do something — so the scene has
// the screen, as the 49-inch shows it, and where the tour is goes small in the
// floor map's corner (hyper-map/tour ?overlay). Who is on the tour shows in
// the top left corner (tour-row.js), which opens to 「ツアーに参加」 before
// the tour and to 「ツアーから離れる」 on it. The menu stays the site's menu:
// lit or locked, it read as the tour's itinerary, and the tour's places are
// in the information panel's list instead. The round information panel
// stays, for the visitor to open on the tour as anywhere else. Leaving puts
// everything back. The list is
// the venue screens' own (hyper-tour-follow SCREEN_HIDES) and what the four
// spaces' pages carry besides; an id absent from a page matches nothing.
//
// A visitor watches (/hypertour/) or joins; there is no following without
// joining (decision-2026-10-02-planning-no-follow-only-mode). Joining is
// being in the flock: the visitor's face is published into the room and seen
// on the lounge's wall (tour/visitor.js), and the camera follows the tour —
// following is how being in the flock moves the view, kept as its own key.
// The row's way in says `hyper-tour:join`; this file keeps the tab's
// `hyper-tour-participating` and says `hyper-tour:participating { joined }`
// whenever it changes. Every way off — the row's, a drag that takes the
// camera — ends joining.
//
// The beat's size, period and colour, the plan's size and the wording are
// provisional values for haruma to look at.

import { SCREEN_KEY, screenMark } from '../lib/roles.js';
import { rowOffers, tourRow } from './tour-row.js';
import { cornerPlan } from './tour-plan.js';
import { JOINING_OPEN } from './visitor.js';
import { facePicker } from './face-picker.js';
import { dressTheVeil } from './carry-view.js';
import { REBUILDS, REBUILD_KEY, REBUILD_WINDOW_MS, VEIL_RELOADS_KEY, crossingStep } from '../lib/stuck-crossing.js';
import { mayReload } from './restore-reload.js';
import { markCrossing, navigationType, settleArrival } from './crossing-mark.js';

/** A page the visitor arrived on by a crossing that stays behind its veil
 *  with nothing arriving for it (lib/stuck-crossing.js crossingStep) is
 *  loaded again, at its own address (which carries where the tour is),
 *  within the bound reloads keep — not left on "Loading" until the next
 *  crossing, and not reloaded while it is still coming in. The clock stands
 *  still while the page is hidden, which paints and settles nothing. */
function retryIfStuck() {
  const veil = document.getElementById( 'loading' );
  if ( !veil ) return;
  let since = Date.now();
  let progressAt = null;
  const progress = () => { progressAt = Date.now(); };
  let observer = null;
  try {
    observer = new PerformanceObserver( progress );
    observer.observe( { type: 'resource' } );
  } catch ( _err ) { /* no observer: the scene's steps below are the progress */ }
  const scene = document.querySelector( 'a-scene' );
  if ( scene ) for ( const step of [ 'loaded', 'renderstart' ] ) scene.addEventListener( step, progress );
  const watch = setInterval( () => {
    const cs = veil.isConnected ? getComputedStyle( veil ) : null;
    if ( !cs || cs.display === 'none' || parseFloat( cs.opacity || '1' ) < 0.01 ) {
      clearInterval( watch );
      if ( observer ) observer.disconnect();
      return;
    }
    if ( document.hidden ) { since = Date.now(); progressAt = null; return; }
    const now = Date.now();
    if ( crossingStep( { since, progressAt }, now ) === 'wait' ) return;
    clearInterval( watch );
    if ( observer ) observer.disconnect();
    const why = `${ location.pathname } veiled ${ Math.round( ( now - since ) / 1000 ) } s, nothing for ${ Math.round( ( now - ( progressAt || since ) ) / 1000 ) } s`;
    let stamps = [];
    try { stamps = JSON.parse( window.sessionStorage.getItem( REBUILD_KEY ) || '[]' ); } catch ( _err ) { stamps = []; }
    const { allowed, recent } = mayReload( stamps, now, REBUILDS, REBUILD_WINDOW_MS );
    if ( !allowed ) { console.warn( `[tour-nav] ${ why }; ${ recent.length } reloads already, waiting on` ); return; }
    try {
      window.sessionStorage.setItem( REBUILD_KEY, JSON.stringify( [ ...recent, now ] ) );
      const said = JSON.parse( window.sessionStorage.getItem( VEIL_RELOADS_KEY ) || '[]' );
      window.sessionStorage.setItem( VEIL_RELOADS_KEY, JSON.stringify( [ why, ...said ].slice( 0, 4 ) ) );
    } catch ( _err ) { return; }
    console.warn( `[tour-nav] ${ why }; loading again` );
    markCrossing( location.pathname );
    window.location.reload();
  }, 1000 );
}

const FOLLOWING_KEY = 'hyper-tour-following';

// A screen is always a space in a shell's frame (lib/roles.js screenMark).
// At the top of its tab the mark is one left behind — a tab killed before
// /hypertour/'s pagehide, restored on a gallery page — and the page would
// run as a screen: no bar, no menu's way out, nothing published. Taken off
// as this module runs, before the page's scene reads it.
// And arriving here other than by the tour's own crossing (tour/crossing-
// mark.js) is the visitor's own move: the tour's marks go, before the scene
// or the row reads them, and this opens as an ordinary page.
if ( window.top === window ) {
  try { window.sessionStorage.removeItem( SCREEN_KEY ); } catch ( _err ) { /* nothing kept */ }
  settleArrival( { navigationType: navigationType() } );
}
export const PARTICIPATING_KEY = 'hyper-tour-participating';
const PARTICIPATING_EVENT = 'hyper-tour:participating';
const BEAT = { from: 1, to: 0.72, periodS: 2.4, color: '#ff7a1a' };
// Leaving closes a circle in the black onto where /hypertour/'s circle stands
// (its join opens the same circle), then the black lifts off the page the
// visitor stays on. Provisional values for haruma to look at.
const CLOSE = { ms: 900, liftMs: 400, y: '46vh' };
const FOLDED = [
  '#info', '#title', '#sound', '#timetable', 'body .map',
  '#cardList', '#categoryList', '#controlPanel', '#modeswitch',
  '#hyper-icc-radio-hint', '#mikan-download-panel', '#sculpture-list-panel', '#pareidolia-list-panel',
];

function joinedNow() {
  try {
    return window.sessionStorage.getItem( FOLLOWING_KEY ) === '1';
  } catch ( _err ) {
    return false;   // blocked storage: nothing remembers a join
  }
}

function addStyle() {
  const style = document.createElement( 'style' );
  style.textContent = `
    @keyframes hyper-tour-beat {
      0%, 100% { transform: scale(${ BEAT.from }); }
      50% { transform: scale(${ BEAT.to }); }
    }
    nav.on-tour label div {
      background-color: ${ BEAT.color } !important;
      animation: hyper-tour-beat ${ BEAT.periodS }s ease-in-out infinite;
    }
    html.on-tour :is(${ FOLDED.join( ', ' ) }) { display: none !important; }
    .tour-plan { display: none; }
    html.on-tour .tour-plan { display: block; }
    @property --leave-r { syntax: "<length>"; inherits: false; initial-value: 0px; }
    div.tour-leaving {
      position: fixed; inset: 0; z-index: 99990; opacity: 1; --leave-r: 150vmax;
      background: radial-gradient( circle var( --leave-r ) at 50% ${ CLOSE.y },
        transparent calc( 100% - 1px ), #000 100% );
      transition: --leave-r ${ CLOSE.ms }ms ease-out, opacity ${ CLOSE.liftMs }ms ease;
    }
    /* On iPhone Safari's stage the black spans the document (edge-to-edge.css). */
    html.hyper-edge-to-edge div.tour-leaving {
      position: absolute; top: 0; bottom: auto; height: var( --hyper-stage-height );
      background: radial-gradient( circle var( --leave-r ) at 50% calc( var( --hyper-runway ) + ${ CLOSE.y } ),
        transparent calc( 100% - 1px ), #000 100% );
    }
    div.tour-leaving.closed { --leave-r: 0px; }
    /* Under the row (tour-row.js), in its ground, off every edge: a fixed box
       on an edge of a staged page stops it compositing into the band. */
    div.tour-next-loading {
      position: fixed; z-index: 9690; left: 8px; top: calc(56px + env(safe-area-inset-top));
      padding: 0.2rem 0.7rem; border-radius: 999px; background: rgba(0, 0, 0, 0.55); color: #f4ead8;
      font-size: 0.7rem; letter-spacing: 0.02em; opacity: 1; pointer-events: none;
    }
    div.tour-next-loading[hidden] { display: none; }
    @media screen and (max-width: 767px) {
      div.tour-next-loading { top: calc(48px + env(safe-area-inset-top)); }
    }
    div.tour-leaving.lifted { opacity: 0; }
  `;
  document.head.appendChild( style );
}

/** Spans of both languages, with the one the page is not showing hidden the
 *  way langswitch hid the menu's own (copied from the first it reached). */
function inPageLang( el, nav ) {
  const shown = nav.querySelector( '.menu-container span[lang]:not([style*="none"])' );
  const lang = shown ? shown.getAttribute( 'lang' ) : document.documentElement.lang || 'ja';
  for ( const span of el.querySelectorAll( 'span[lang]' ) ) {
    if ( span.getAttribute( 'lang' ) !== lang ) span.style.display = 'none';
  }
  return el;
}

/** A line under the row while the next space is fetched ahead
 *  (tour/prefetch-next.js, said by hyper-tour-follow): the walk stays in
 *  view, and if it stutters meanwhile the visitor can see why. */
function nextLoading( nav ) {
  const line = document.createElement( 'div' );
  line.className = 'tour-next-loading';
  line.hidden = true;
  line.innerHTML = '<span lang="ja">次の空間を読み込み中…</span><span lang="en">Loading the next space…</span>';
  document.body.appendChild( inPageLang( line, nav ) );
  window.addEventListener( 'hyper-tour:prefetching', ( e ) => { line.hidden = !( e.detail && e.detail.on ); } );
}

/** The row's way off: the circle closes in the black, the tour lets go,
 *  and the black lifts off the page the visitor stays on. */
function leaveLink( nav ) {
  const leave = document.createElement( 'a' );
  leave.href = '#';
  leave.className = 'leave';
  leave.innerHTML = '<span lang="ja">ツアーから離れる</span><span lang="en">Leave the tour</span>';
  let leaving = false;
  inPageLang( leave, nav ).addEventListener( 'click', ( e ) => {
    e.preventDefault();
    if ( leaving ) return;
    leaving = true;
    const black = document.createElement( 'div' );
    black.className = 'tour-leaving';
    document.body.appendChild( black );
    void black.offsetWidth;
    black.classList.add( 'closed' );
    setTimeout( () => {
      try { window.sessionStorage.removeItem( FOLLOWING_KEY ); } catch ( _err ) { /* nothing kept */ }
      leftHere = true;
      participate( false );
      window.dispatchEvent( new CustomEvent( 'hyper-tour:leave' ) );
      show( nav, false );
      black.classList.add( 'lifted' );
      setTimeout( () => { black.remove(); leaving = false; }, CLOSE.liftMs );
    }, CLOSE.ms );
  } );
  return leave;
}

/** The row's way on: into the flock, which follows the tour too — there is
 *  no following without joining
 *  (decision-2026-10-02-planning-no-follow-only-mode). */
function joinLink( nav ) {
  if ( !JOINING_OPEN ) return null;
  const join = document.createElement( 'a' );
  join.href = '#';
  join.className = 'join';
  join.innerHTML = '<span lang="ja">ツアーに参加</span><span lang="en">Join the tour</span>';
  inPageLang( join, nav ).addEventListener( 'click', ( e ) => {
    e.preventDefault();
    window.dispatchEvent( new CustomEvent( 'hyper-tour:join' ) );
  } );
  return join;
}

/** The language the page is showing, the way langswitch chose it: `?lang=`,
 *  else the browser's. */
function pageLang() {
  const asked = new URLSearchParams( location.search ).get( 'lang' );
  return ( asked || navigator.language || 'en' ).slice( 0, 2 ) === 'ja' ? 'ja' : 'en';
}

let row = null;
let plan = null;
let join = null;
let leave = null;
// What the follow component offers a visitor not on the tour (hyper-tour:offer).
let offered = window.hyperTourOffer || { offer: null, face: '🚩' };
// Left the tour on this page: the corner stays empty here. The way back is
// the top page's tag and /hypertour/; the next page, or a page the visitor
// never left from, offers the way on as before.
let leftHere = false;

function show( nav, joined ) {
  nav.classList.toggle( 'on-tour', joined );
  document.documentElement.classList.toggle( 'on-tour', joined );
  paintRow( nav );
}

/** The row in the corner: on the tour, the faces off the plan; before it,
 *  where joining is open and the tour is to be had, the guide's face and the
 *  way in — except on a page whose way in is the tour's tag (the top page,
 *  tour-tag.js), where a second thing coming and going by the heading read
 *  as noise. Not on a screen: a space in /hypertour/'s frame would put a
 *  second plan and row under the shell's. */
function paintRow( nav ) {
  const onTour = joinedNow();
  const tagged = document.documentElement.dataset.tourTag !== undefined;
  const wanted = onTour || ( JOINING_OPEN && !!offered.offer && !tagged && !leftHere );
  if ( screenMark() || ( !wanted && !row ) ) return;
  if ( !row ) {
    join = joinLink( nav );
    leave = leaveLink( nav );
    // The face to join with, and to change on the tour, beside the ways:
    // only where the scene has its own picker to say it (face-picker.js).
    const picker = JOINING_OPEN && document.getElementById( 'emoji' ) ? facePicker() : null;
    row = tourRow( {
      actions: [ join, leave ].filter( Boolean ),
      picker,
      lookAtGuide: true,
    } );
  }
  // The plan (tour-plan.js) is loaded the first time the tab is on the tour:
  // it is a page of its own.
  if ( onTour && !plan ) {
    plan = cornerPlan( pageLang() );
    document.body.appendChild( plan.box );
    row.watch( plan.frame );
  }
  row.faces( [ offered.face ] );
  row.guideOnly( !onTour );
  const offers = rowOffers( {
    joiningOpen: JOINING_OPEN, onTour, participating: participatingNow(), offered: !!offered.offer && !tagged,
  } );
  if ( join ) join.hidden = !offers.join;
  leave.hidden = !offers.leave;
  row.actionsChanged();
  row.show( wanted );
}

function participatingNow() {
  try {
    return window.sessionStorage.getItem( PARTICIPATING_KEY ) === '1';
  } catch ( _err ) {
    return false;
  }
}

/** Join the flock or leave it, and say so; nothing said when nothing changes. */
function participate( on ) {
  if ( participatingNow() === on ) return;
  try {
    if ( on ) window.sessionStorage.setItem( PARTICIPATING_KEY, '1' );
    else window.sessionStorage.removeItem( PARTICIPATING_KEY );
  } catch ( _err ) { /* nothing kept: the next page arrives watching */ }
  const nav = document.querySelector( 'nav' );
  if ( nav ) paintRow( nav );
  window.dispatchEvent( new CustomEvent( PARTICIPATING_EVENT, { detail: { joined: on } } ) );
}

function start() {
  // Arriving from /hypertour/'s Join: its picture of the tour behind the veil.
  // On the tour (a crossing, a join): whatever covers it until the camera is
  // on the tour, and a retry if the page never comes up.
  const arriving = joinedNow() && !screenMark();
  dressTheVeil( { onTour: arriving } );
  if ( arriving ) retryIfStuck();
  // Joined but not on the tour is a state no page offers a way out of: the
  // room has the visitor's face, the menu says they are not on the tour.
  // Arriving so (from /hypertour/'s exit, say), the joining goes too.
  if ( participatingNow() && !joinedNow() ) participate( false );
  const nav = document.querySelector( 'nav' );
  if ( nav ) {
    addStyle();
    show( nav, joinedNow() );
    if ( !screenMark() ) nextLoading( nav );
  }
  // A page restored from the back-forward cache comes back as it was left —
  // folded for a tour this tab may have left since (Safari keeps pages with
  // sockets open; the room's own reconnect brings the rest back).
  window.addEventListener( 'pageshow', ( e ) => {
    if ( !e.persisted ) return;
    if ( participatingNow() && !joinedNow() ) participate( false );
    if ( nav ) show( nav, joinedNow() );
  } );
  window.addEventListener( 'hyper-tour:offer', ( e ) => {
    offered = e.detail || { offer: null, face: '🚩' };
    if ( nav ) paintRow( nav );
  } );
  window.addEventListener( 'hyper-tour:joined', ( e ) => {
    const joined = !!( e.detail && e.detail.joined );
    if ( nav ) show( nav, joined );
    // Stopping following, by the menu or by taking the camera, is leaving the flock.
    if ( !joined ) participate( false );
  } );
  // The row's way in: into the flock, and after the tour.
  window.addEventListener( 'hyper-tour:join', () => {
    participate( true );
    const scene = document.querySelector( 'a-scene' );
    const follow = scene && scene.components && scene.components[ 'hyper-tour-follow' ];
    if ( follow && !follow.joined ) follow.follow();
  } );
}

if ( document.readyState === 'loading' ) document.addEventListener( 'DOMContentLoaded', start );
else start();
